import { useId, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetEvidenceQuery } from "~/operations";

export interface FilePathProps {
  scopeMrn: string | undefined;
  findingMrn: string | undefined;
  ecosystem?: string | undefined;
  label?: string;
}

export const FilePath = ({
  scopeMrn,
  findingMrn,
  ecosystem,
  label = "This vulnerability exists here:",
}: FilePathProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const { data } = useGetEvidenceQuery({
    variables: {
      input: {
        scopeMrn: scopeMrn!,
        findingMrn: findingMrn!,
        ...(ecosystem && { filter: { ecosystem } }),
      },
    },
    skip: !scopeMrn || !findingMrn,
  });

  const paths =
    data?.evidence?.items
      .map((item) =>
        item.details && "path" in item.details ? item.details.path : undefined,
      )
      .filter((path): path is string => Boolean(path)) || [];

  if (paths.length === 0) return null;

  const hasMultiplePaths = paths.length > 1;
  const selectedPath = paths[selectedIndex];
  const otherPaths = paths.filter((_, index) => index !== selectedIndex);

  const handleToggle = () => {
    if (hasMultiplePaths) {
      setExpanded(!expanded);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (hasMultiplePaths && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      handleToggle();
    }
  };

  const handleSelectPath = (path: string) => {
    const newIndex = paths.indexOf(path);
    setSelectedIndex(newIndex);
    setExpanded(false);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Typography
        variant="body1"
        color="text.primary"
        sx={{ flexShrink: 0 }}
        id={`${listId}-label`}
      >
        {label}
      </Typography>
      <Box>
        <Box
          role={hasMultiplePaths ? "button" : undefined}
          tabIndex={hasMultiplePaths ? 0 : undefined}
          aria-expanded={hasMultiplePaths ? expanded : undefined}
          aria-controls={hasMultiplePaths ? listId : undefined}
          aria-labelledby={`${listId}-label`}
          onKeyDown={handleKeyDown}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: hasMultiplePaths ? "pointer" : "default",
          }}
          onClick={handleToggle}
        >
          <code>{selectedPath}</code>
          {hasMultiplePaths && (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
                aria-label={`${paths.length} file paths available`}
              >
                <Box component="span" aria-hidden="true">
                  ({paths.length})
                </Box>
              </Typography>
              <IconButton
                size="small"
                aria-label={
                  expanded ? "Collapse file paths" : "Expand file paths"
                }
                aria-expanded={expanded}
                aria-controls={listId}
                tabIndex={-1}
                sx={{
                  p: 0,
                  ml: 0.5,
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle();
                }}
              >
                <ExpandMoreIcon fontSize="small" color="primary" />
              </IconButton>
            </>
          )}
        </Box>
        {hasMultiplePaths && (
          <Collapse in={expanded}>
            <List
              id={listId}
              dense
              disablePadding
              role="listbox"
              aria-label="Available file paths"
              sx={{ mt: 0.5 }}
            >
              {otherPaths.map((path) => (
                <ListItemButton
                  key={path}
                  role="option"
                  onClick={() => handleSelectPath(path)}
                  sx={{ py: 0.5, px: 1, borderRadius: 1 }}
                >
                  <code>{path}</code>
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};
